import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  prjDeptHeaderTyps,
  prjRealSalesHeaderTyps,
  prjSalesHeaderTyps,
  prjDeptHeaders,
  prjStatHeaderTyps,
  prjTargetRateHeaderTyps,
  prjExTargetRateHeaderTyps,
} from '../../utils/forecastStaticData';
import SalesStaticsEmpRows from './SalesStaticsEmpRows';
import SalesDetailDrawer from './SalesDetailDrawer';

const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      // [핵심] \n 문자를 줄바꿈으로 렌더링하고, 셀 너비를 넘치면 자동 줄바꿈
      whiteSpace: 'pre-wrap', 
      wordBreak: 'break-all', 
      overflow: 'visible',
      textOverflow: 'clip',
      padding: '6px 4px',
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
    },
  },
});

const SalesStaticsEmpTable = ({ 
  data, 
  tableRef, 
  selectPrjTyp, 
  strMonth, 
  endMonth,
  onCellClick,
  detailData,
  detailLoading 
}) => {
  const classes = useStyles();
  const [showKorean, setShowKorean] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);

  const getBgColor = (id) => {
    if (id?.includes('STAT')) return '#b0deff';
    if (id === 'OA') return '#e3f2fd';
    if (id === 'HM') return '#fff3e0';
    if (id === 'ES') return '#e8f5e9';
    return '#f5f5f5';
  };

  const handleInternalClick = (cellData, field, type) => {
    setSelectedInfo({ cellData, field, type });
    setDrawerOpen(true);
    if (onCellClick) onCellClick(cellData, field, type);
  };

  const baseHeaders = prjDeptHeaders(strMonth, endMonth, '매출');
  const filteredHeaders = baseHeaders.filter((header) => 
    header.id.includes('STAT') ? selectPrjTyp === 'A' : true
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, px: 1 }}>
        <ToggleButtonGroup
          value={showKorean}
          exclusive
          onChange={(e, nextValue) => { if (nextValue !== null) setShowKorean(nextValue); }}
          size="small"
          sx={{
            height: '32px',
            '& .MuiToggleButton-root': {
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#455a64',
              px: 1.5,
              border: '1px solid #cfd8dc', 
              '&.Mui-selected': {
                backgroundColor: '#1967d2',
                color: 'white',
              },
            },
          }}
        >
          <ToggleButton value={false}>숫자 적용</ToggleButton>
          <ToggleButton value={true}>한글 적용</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="caption" sx={{ color: '#455a64', fontWeight: 700, fontSize: '0.75rem', marginLeft: 'auto' }}>
          (단위: {showKorean ? '한글 혼용' : '원'})
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', my: 1 }}>
        <TableContainer 
          sx={{ 
            width: '100%', 
            overflowX: 'auto', // 내용이 너무 많아지면 스크롤 발생
          }}
        >
          <Table 
            size="small" 
            className={classes.table} 
            ref={tableRef} 
            stickyHeader
            sx={{ 
              tableLayout: 'fixed', 
              width: '100%', // 화면 너비 100% 채우기
            }}
          >
            <TableHead>
              <TableRow>
                {['본부', '팀', '사원'].map((label) => (
                  <TableCell 
                    key={label} align="center" rowSpan={2} 
                    sx={{ 
                      bgcolor: '#f5f5f5', fontSize: '0.85rem', fontWeight: 'bold', 
                      width: '120px', minWidth: '120px' // 좌측 고정
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
                {filteredHeaders.map((header) => (
                  <TableCell
                    key={header.val} align="center"
                    colSpan={header.id === 'OA' ? 7 : 2}
                    sx={{ 
                      bgcolor: getBgColor(header.id), fontWeight: 800,
                      width: 'auto' // 남은 공간 균등 배분
                    }}
                  >
                    {header.name}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                {filteredHeaders.map((header) => {
                  const headerTyp =
                    header?.id === 'EXSTATRATE' ? prjExTargetRateHeaderTyps :
                    header?.id === 'STATRATE' ? prjTargetRateHeaderTyps : 
                    header?.id?.includes('STAT') ? prjStatHeaderTyps : 
                    header?.id === 'OA' ? prjRealSalesHeaderTyps : 
                    header?.id === 'HM' ? prjDeptHeaderTyps : 
                    prjSalesHeaderTyps;

                  return headerTyp.map((typ, index) => (
                    <TableCell 
                      key={`${header.id}-${index}`} align="center" 
                      sx={{ 
                        bgcolor: getBgColor(header.id), fontSize: '0.8rem', fontWeight: 700,
                        width: 'auto' 
                      }}
                    >
                      {typ.name}
                    </TableCell>
                  ));  
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.isArray(data) && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={50} align="center" sx={{ py: 5 }}>데이터가 없습니다.</TableCell>
                </TableRow>
              ) : (
                <SalesStaticsEmpRows
                  headerRows={filteredHeaders} 
                  data={data} 
                  useKorean={showKorean}
                  selectPrjTyp={selectPrjTyp}
                  onCellClick={handleInternalClick}
                  clickOn={true}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SalesDetailDrawer 
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedDetail={selectedInfo}
        data={detailData}
        loading={detailLoading}
      />
    </Box>
  );
};

export default SalesStaticsEmpTable;
